import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc, 
  Timestamp,
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { 
  updateProfile as updateFirebaseProfile, 
  updateEmail as updateFirebaseEmail,
  updatePassword as updateFirebasePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  User
} from 'firebase/auth';
import { db, auth } from './firebase.config';
import { UserProfile } from '../types';

export interface UpdateProfileData {
  name?: string;
  email?: string;
  preferences?: {
    emailNotifications?: boolean;
    pushNotifications?: boolean;
  };
  jobPreferences?: {
    positions?: string[];
    description?: string;
    experienceLevel?: 'Junior' | 'Mid' | 'Senior' | 'Lead' | 'Manager';
    preferredSector?: string;
    remotePreference?: 'On-site' | 'Hybrid' | 'Remote' | 'Flexible';
  };
}

export interface UpdatePasswordData {
  currentPassword: string;
  newPassword: string;
}

class UserService {
  // Recupera il profilo utente da Firestore
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      console.log('👤 Recupero profilo utente:', userId);
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const profile = userDoc.data() as UserProfile;
        console.log('✅ Profilo utente recuperato');
        return profile;
      } else {
        console.log('❌ Profilo utente non trovato');
        return null;
      }
    } catch (error) {
      console.error('❌ Errore nel recupero profilo utente:', error);
      throw new Error('Impossibile recuperare il profilo utente');
    }
  }

  // Aggiorna il profilo utente in Firestore
  async updateUserProfile(userId: string, data: UpdateProfileData): Promise<UserProfile> {
    try {
      console.log('📝 Aggiornamento profilo utente:', userId, data);
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('Profilo utente non trovato');
      }
      
      const currentProfile = userDoc.data() as UserProfile;
      
      // Prepara i dati di aggiornamento
      const updateData: any = {
        ...data,
        updatedAt: Timestamp.now()
      };
      
      // Aggiorna le preferenze se fornite
      if (data.preferences) {
        updateData.preferences = {
          ...currentProfile.preferences,
          ...data.preferences
        };
      }

      // Aggiorna le job preferences se fornite
      if (data.jobPreferences) {
        updateData.jobPreferences = {
          ...currentProfile.jobPreferences,
          ...data.jobPreferences
        };
      }
      
      await updateDoc(userDocRef, updateData);
      
      // Recupera il profilo aggiornato
      const updatedDoc = await getDoc(userDocRef);
      const updatedProfile = updatedDoc.data() as UserProfile;
      
      console.log('✅ Profilo utente aggiornato');
      return updatedProfile;
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento profilo:', error);
      throw new Error('Impossibile aggiornare il profilo utente');
    }
  }

  // Aggiorna il nome utente in Firebase Auth e Firestore
  async updateUserName(user: User, newName: string): Promise<void> {
    try {
      console.log('📝 Aggiornamento nome utente:', newName);
      
      // Aggiorna Firebase Auth
      await updateFirebaseProfile(user, {
        displayName: newName
      });
      
      // Aggiorna Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        name: newName,
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Nome utente aggiornato');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento nome:', error);
      throw new Error('Impossibile aggiornare il nome utente');
    }
  }

  // Aggiorna l'email utente in Firebase Auth e Firestore
  async updateUserEmail(user: User, newEmail: string, currentPassword: string): Promise<void> {
    try {
      console.log('📝 Aggiornamento email utente:', newEmail);
      
      // Riautentica l'utente prima di cambiare email
      const credential = EmailAuthProvider.credential(user.email || '', currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Aggiorna Firebase Auth
      await updateFirebaseEmail(user, newEmail);
      
      // Aggiorna Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        email: newEmail,
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Email utente aggiornata');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento email:', error);
      throw new Error('Impossibile aggiornare l\'email utente');
    }
  }

  // Aggiorna la password utente
  async updateUserPassword(user: User, currentPassword: string, newPassword: string): Promise<void> {
    try {
      console.log('📝 Aggiornamento password utente');
      
      // Riautentica l'utente
      const credential = EmailAuthProvider.credential(user.email || '', currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Aggiorna la password
      await updateFirebasePassword(user, newPassword);
      
      console.log('✅ Password utente aggiornata');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento password:', error);
      throw new Error('Impossibile aggiornare la password');
    }
  }

  // Crea un nuovo profilo utente in Firestore
  async createUserProfile(userId: string, profile: Omit<UserProfile, 'createdAt' | 'updatedAt'>): Promise<UserProfile> {
    try {
      console.log('📝 Creazione profilo utente:', userId);
      
      const userDocRef = doc(db, 'users', userId);
      const newProfile: UserProfile = {
        ...profile,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      await setDoc(userDocRef, newProfile);
      
      console.log('✅ Profilo utente creato');
      return newProfile;
    } catch (error) {
      console.error('❌ Errore nella creazione profilo:', error);
      throw new Error('Impossibile creare il profilo utente');
    }
  }

  // Elimina il profilo utente
  async deleteUserProfile(userId: string): Promise<void> {
    try {
      console.log('🗑️ Eliminazione profilo utente:', userId);
      
      // Nota: In un'implementazione completa, qui dovresti anche:
      // 1. Eliminare tutte le candidature dell'utente
      // 2. Eliminare le notifiche
      // 3. Eliminare i file di storage
      // 4. Eliminare l'account Firebase Auth
      
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        deletedAt: Timestamp.now(),
        isDeleted: true
      });
      
      console.log('✅ Profilo utente eliminato');
    } catch (error) {
      console.error('❌ Errore nell\'eliminazione profilo:', error);
      throw new Error('Impossibile eliminare il profilo utente');
    }
  }

  // Ottieni tutti gli utenti (per admin/moderatori)
  async getAllUsers(): Promise<{ id: string; name: string; email: string; role?: string }[]> {
    try {
      console.log('👥 Recupero lista utenti');
      
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(usersQuery);
      
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'Utente senza nome',
        email: doc.data().email || '',
        role: doc.data().role || 'user'
      }));
      
      console.log('✅ Lista utenti recuperata:', users.length);
      return users;
    } catch (error) {
      console.error('❌ Errore nel recupero lista utenti:', error);
      throw new Error('Impossibile recuperare la lista utenti');
    }
  }
}

export const userService = new UserService(); 